import { FormEvent, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Portal } from '../Portal';
import { api, CreditPurchase } from '../../services/api';

interface Props {
  open: boolean;
  defaultName?: string;
  defaultEmail?: string;
  onClose: () => void;
  onPaid: () => void | Promise<void>;
}

const emptyCard = { holderName: '', number: '', expiryMonth: '', expiryYear: '', ccv: '' };

function money(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function messageForStatus(purchase: CreditPurchase) {
  if (purchase.status === 'PAID') return 'Pagamento confirmado e créditos adicionados.';
  if (purchase.status === 'FAILED') return purchase.failureMessage || 'Pagamento não aprovado.';
  if (purchase.status === 'EXPIRED') return 'Esta cobrança expirou.';
  if (purchase.status === 'REFUNDED') return 'Pagamento estornado pelo Asaas.';
  if (purchase.status === 'CHARGEBACK') return 'Pagamento contestado/estornado.';
  if (purchase.providerStatus === 'AWAITING_RISK_ANALYSIS') return 'Pagamento em análise de segurança pelo Asaas.';
  return 'Aguardando a confirmação do Asaas…';
}

export function CreditPurchaseModal({ open, defaultName = '', defaultEmail = '', onClose, onPaid }: Props) {
  const [credits, setCredits] = useState(100);
  const [billingType, setBillingType] = useState<'PIX' | 'CREDIT_CARD'>('PIX');
  const [buyer, setBuyer] = useState({ name: defaultName, email: defaultEmail, cpfCnpj: '', phone: '', postalCode: '', addressNumber: '', addressComplement: '' });
  const [card, setCard] = useState(emptyCard);
  const [purchase, setPurchase] = useState<CreditPurchase | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const firstInput = useRef<HTMLInputElement>(null);
  const paidNotified = useRef<string | null>(null);

  const terminal = purchase && ['PAID', 'FAILED', 'EXPIRED', 'REFUNDED', 'CHARGEBACK'].includes(purchase.status);

  useEffect(() => {
    if (!open) return;
    setBuyer(current => ({ ...current, name: current.name || defaultName, email: current.email || defaultEmail }));
    setRecovering(true);
    api.getLatestPendingCreditPurchase()
      .then(response => { if (response.data) setPurchase(response.data); })
      .catch(() => { /* não há compra recuperável ou usuário sem permissão */ })
      .finally(() => setRecovering(false));
    const timer = window.setTimeout(() => firstInput.current?.focus(), 100);
    return () => window.clearTimeout(timer);
  }, [open, defaultName, defaultEmail]);

  useEffect(() => {
    if (!open || !purchase || terminal) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await api.getCreditPurchase(purchase.id);
        if (!cancelled) setPurchase(response.data);
      } catch { /* o webhook e a próxima consulta tentarão novamente */ }
    };
    const interval = window.setInterval(poll, 3_000);
    const onFocus = () => { void poll(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [open, purchase?.id, terminal]);

  useEffect(() => {
    if (purchase?.status !== 'PAID' || paidNotified.current === purchase.id) return;
    paidNotified.current = purchase.id;
    toast.success(`${purchase.credits} créditos adicionados!`);
    void onPaid();
  }, [purchase?.status, purchase?.id, purchase?.credits, onPaid]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const reset = () => {
    setPurchase(null);
    setError('');
    setCard(emptyCard);
    setIdempotencyKey(crypto.randomUUID());
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!Number.isInteger(credits) || credits < 100) return setError('A compra mínima é de 100 créditos.');
    setSubmitting(true);
    try {
      const response = await api.createCreditPurchase({
        credits,
        billingType,
        idempotencyKey,
        buyer,
        ...(billingType === 'CREDIT_CARD' ? { creditCard: card } : {}),
      });
      setPurchase(response.data);
      setCard(emptyCard);
    } catch (rawError) {
      setCard(emptyCard);
      setError(rawError instanceof Error ? rawError.message : 'Não foi possível iniciar o pagamento.');
      setIdempotencyKey(crypto.randomUUID());
    } finally {
      setSubmitting(false);
    }
  };

  const copyPix = async () => {
    if (!purchase?.pix?.payload) return;
    await navigator.clipboard.writeText(purchase.pix.payload);
    toast.success('Código PIX copiado.');
  };

  const inputClass = 'mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100';

  return (
    <Portal>
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/60 p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="credit-purchase-title" onMouseDown={event => { if (event.target === event.currentTarget && !submitting) onClose(); }}>
        <div className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-slate-50 shadow-2xl">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 sm:px-7">
            <div>
              <h2 id="credit-purchase-title" className="text-lg font-black text-slate-900">Comprar mais créditos</h2>
              <p className="text-xs text-slate-500">1 crédito = R$ 1,00 · compra mínima de R$ 100,00</p>
            </div>
            <button type="button" onClick={onClose} disabled={submitting} aria-label="Fechar" className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40">✕</button>
          </div>

          <div className="p-5 sm:p-7">
            {recovering && !purchase ? (
              <p className="py-14 text-center text-sm text-slate-500 animate-pulse">Verificando pagamentos em andamento…</p>
            ) : purchase ? (
              <div className="space-y-5" aria-live="polite">
                <div className={`rounded-2xl border p-5 text-center ${purchase.status === 'PAID' ? 'border-emerald-200 bg-emerald-50' : terminal ? 'border-red-200 bg-red-50' : 'border-indigo-200 bg-indigo-50'}`}>
                  <div className="text-3xl">{purchase.status === 'PAID' ? '✓' : terminal ? '!' : '◌'}</div>
                  <p className="mt-2 text-base font-black text-slate-900">{messageForStatus(purchase)}</p>
                  <p className="mt-1 text-sm text-slate-600">{purchase.credits} créditos · {money(purchase.amountCents)}</p>
                </div>

                {purchase.billingType === 'PIX' && purchase.pix && !terminal && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                    {purchase.pix.encodedImage && <img className="mx-auto h-56 w-56 rounded-xl" alt="QR Code PIX" src={purchase.pix.encodedImage.startsWith('data:') ? purchase.pix.encodedImage : `data:image/png;base64,${purchase.pix.encodedImage}`} />}
                    <p className="mt-3 text-xs font-semibold text-slate-600">Escaneie o QR Code ou copie o código PIX</p>
                    <button type="button" onClick={copyPix} className="mt-3 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700">Copiar PIX copia e cola</button>
                    {purchase.pix.expirationDate && <p className="mt-2 text-[11px] text-slate-400">Válido até {new Date(purchase.pix.expirationDate).toLocaleString('pt-BR')}</p>}
                  </div>
                )}

                {!terminal && <p className="text-center text-xs text-slate-500">Esta tela é atualizada automaticamente após o webhook confirmado pelo Asaas. Você pode fechá-la e voltar depois.</p>}
                <div className="flex justify-end gap-2">
                  {terminal && purchase.status !== 'PAID' && <button type="button" onClick={reset} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-100">Tentar novamente</button>}
                  <button type="button" onClick={onClose} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800">{purchase.status === 'PAID' ? 'Concluir' : 'Fechar'}</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" autoComplete="on">
                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500" htmlFor="credit-amount">Quantidade de créditos</label>
                  <div className="mt-2 flex items-center gap-3">
                    <input ref={firstInput} id="credit-amount" type="number" min={100} max={1000000} step={1} value={credits} onChange={event => setCredits(Number(event.target.value))} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-xl font-black outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" required />
                    <div className="min-w-28 text-right"><p className="text-xs text-slate-400">Total</p><p className="font-black text-slate-900">{money(Number.isFinite(credits) ? credits * 100 : 0)}</p></div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">{[100, 250, 500, 1000].map(value => <button key={value} type="button" onClick={() => setCredits(value)} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${credits === value ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{value}</button>)}</div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Forma de pagamento</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setBillingType('PIX')} className={`rounded-xl border p-3 text-sm font-bold ${billingType === 'PIX' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600'}`}>PIX</button>
                    <button type="button" onClick={() => setBillingType('CREDIT_CARD')} className={`rounded-xl border p-3 text-sm font-bold ${billingType === 'CREDIT_CARD' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600'}`}>Cartão à vista</button>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Dados do pagador</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <label className="text-xs font-semibold text-slate-600">Nome completo<input className={inputClass} value={buyer.name} onChange={e => setBuyer({ ...buyer, name: e.target.value })} autoComplete="name" required /></label>
                    <label className="text-xs font-semibold text-slate-600">E-mail<input className={inputClass} type="email" value={buyer.email} onChange={e => setBuyer({ ...buyer, email: e.target.value })} autoComplete="email" required /></label>
                    <label className="text-xs font-semibold text-slate-600">CPF ou CNPJ<input className={inputClass} inputMode="numeric" value={buyer.cpfCnpj} onChange={e => setBuyer({ ...buyer, cpfCnpj: e.target.value })} required /></label>
                    <label className="text-xs font-semibold text-slate-600">Telefone<input className={inputClass} inputMode="tel" value={buyer.phone} onChange={e => setBuyer({ ...buyer, phone: e.target.value })} autoComplete="tel" required /></label>
                  </div>
                </section>

                {billingType === 'CREDIT_CARD' && (
                  <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-3"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Cartão de crédito</p><span className="text-[11px] font-semibold text-slate-400">Pagamento único · sem parcelamento</span></div>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Nome impresso no cartão<input className={inputClass} value={card.holderName} onChange={e => setCard({ ...card, holderName: e.target.value })} autoComplete="cc-name" required /></label>
                      <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Número do cartão<input className={inputClass} inputMode="numeric" value={card.number} onChange={e => setCard({ ...card, number: e.target.value })} autoComplete="cc-number" required /></label>
                      <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                        <label className="text-xs font-semibold text-slate-600">Mês<input className={inputClass} inputMode="numeric" placeholder="MM" value={card.expiryMonth} onChange={e => setCard({ ...card, expiryMonth: e.target.value })} autoComplete="cc-exp-month" required /></label>
                        <label className="text-xs font-semibold text-slate-600">Ano<input className={inputClass} inputMode="numeric" placeholder="AAAA" value={card.expiryYear} onChange={e => setCard({ ...card, expiryYear: e.target.value })} autoComplete="cc-exp-year" required /></label>
                        <label className="text-xs font-semibold text-slate-600">CVV<input className={inputClass} type="password" inputMode="numeric" value={card.ccv} onChange={e => setCard({ ...card, ccv: e.target.value })} autoComplete="cc-csc" required /></label>
                      </div>
                      <label className="text-xs font-semibold text-slate-600">CEP<input className={inputClass} inputMode="numeric" value={buyer.postalCode} onChange={e => setBuyer({ ...buyer, postalCode: e.target.value })} autoComplete="postal-code" required /></label>
                      <label className="text-xs font-semibold text-slate-600">Número do endereço<input className={inputClass} value={buyer.addressNumber} onChange={e => setBuyer({ ...buyer, addressNumber: e.target.value })} autoComplete="address-line2" required /></label>
                      <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Complemento (opcional)<input className={inputClass} value={buyer.addressComplement} onChange={e => setBuyer({ ...buyer, addressComplement: e.target.value })} /></label>
                    </div>
                  </section>
                )}

                {error && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700" role="alert">{error}</p>}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={onClose} disabled={submitting} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 disabled:opacity-40">Cancelar</button>
                  <button type="submit" disabled={submitting || credits < 100} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-40">{submitting ? 'Processando com o Asaas…' : billingType === 'PIX' ? 'Gerar PIX' : `Pagar ${money(credits * 100)}`}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
